import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  BASIC_BLACK,
  BASIC_DARK_GRAY,
  BASIC_GRAY,
  BASIC_LIGHT_GRAY,
  BASIC_LINE,
  BASIC_ORANGE,
  BASIC_WHITE,
} from "../../constants/colors";
import IconEye from "../../icons/IconEye";
import IconEyeSlash from "../../icons/IconEyeSlash";

type Props = {
  id?: string;
  name: string;
  type?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  labelBorderBottom?: boolean;
  labelExtraText?: string;
  helperText?: string;
  hidePasswordToggle?: boolean;
  hasError?: boolean;
  onChange?: (value: string) => void;
  isNewPassword?: boolean;
  dataWovnIgnore?: boolean;
};

export const Input: React.FC<Props> = ({
  id,
  type = "text",
  name,
  label,
  onChange,
  labelBorderBottom,
  labelExtraText,
  helperText,
  hidePasswordToggle,
  hasError,
  isNewPassword,
  dataWovnIgnore,
  ...props
}) => {
  const formContext = useFormContext();
  const { register, formState } = formContext ?? {};
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const message = formState?.errors[name];
    if (message) {
      setError(
        message
          ? typeof message.message === "string"
            ? message.message
            : ""
          : "",
      );
    } else {
      setError("");
    }
  }, [formState]);

  return (
    <div style={{ position: "relative", fontWeight: "600" }}>
      {label && (
        <Label $labelBorderBottom={labelBorderBottom ?? false}>
          {label}
          {props.required && <span>※</span>}
          {labelExtraText && <OrangeText>{labelExtraText}</OrangeText>}
        </Label>
      )}
      <InputContainer id={`${id}-container`}>
        {dataWovnIgnore && (
          <StyledInput
            id={id ?? `input-${name}`}
            {...register?.(name)}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              register?.(name).onChange(e);
            }}
            type={showPassword ? "text" : type}
            {...props}
            autoComplete={isNewPassword ? "new-password" : "off"}
            $hasError={hasError}
            data-wovn-ignore
          />
        )}
        {!dataWovnIgnore && (
          <StyledInput
            id={id ?? `input-${name}`}
            {...register?.(name)}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              register?.(name).onChange(e);
            }}
            type={showPassword ? "text" : type}
            {...props}
            autoComplete={isNewPassword ? "new-password" : "off"}
            $hasError={hasError}
          />
        )}
        {type === "password" && !hidePasswordToggle && (
          <>
            <IconEyeButton
              onClick={() => setShowPassword((previousValue) => !previousValue)}
            >
              {showPassword ? <IconEyeSlash /> : <IconEye />}
            </IconEyeButton>
          </>
        )}
      </InputContainer>
      <Message $hasError={hasError ?? false}>{helperText}</Message>
    </div>
  );
};

const InputContainer = styled.div`
  position: relative;
  line-height: 20px;
  font-size: 14px;
  margin: 12px 0;
  display: flex;
`;

const Label = styled.label<{ $labelBorderBottom: boolean }>`
  font-weight: 600;
  font-family: "Hiragino Sans";
  ${({ $labelBorderBottom }) =>
    $labelBorderBottom &&
    `
    display: block;
    border-bottom: 1px solid ${BASIC_LINE};
    padding-bottom: 5px;
    margin-bottom: 20px;
  `}
  white-space: pre-line;
  span {
    font-family: "Hiragino Sans";
  }
`;

const Message = styled.div<{ $hasError: boolean }>`
  margin-top: 12px;
  font-size: 12px;
  font-weight: 200;
  white-space: pre-line;
  line-height: 1;
  font-family: "Hiragino Sans";
  color: ${({ $hasError }) => ($hasError ? BASIC_ORANGE : BASIC_BLACK)};
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  width: 90%;
  border-radius: 8px;
  border: solid 1px
    ${({ $hasError }) => ($hasError ? BASIC_ORANGE : BASIC_DARK_GRAY)};
  padding: 14px 23px;
  background-color: ${BASIC_LIGHT_GRAY};
  font-size: 20px;
  font-weight: 400;
  line-height: 1.4em;
  font-family: "Hiragino Sans";
  color: ${BASIC_DARK_GRAY};
  outline: none;
  &::placeholder {
    color: ${BASIC_GRAY};
    font-weight: 300;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:focus {
    background-color: ${BASIC_WHITE};
  }
  ${({ type }) =>
    type === "password" &&
    `
    letter-spacing: 0.05em;
    font-family: "Arial";
    font-size: 20px;
  `}
`;

const IconEyeButton = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 10px;
  outline: none;
  &:focus {
    outline: none;
  }
`;

const OrangeText = styled.span`
  color: ${BASIC_ORANGE};
  font-size: 12px;
  margin-left: 10px;
  font-family: "Hiragino Sans";
`;
